export interface ImportValueModel {
  id: string;
  projectId: string;
  proposalId: string | null;
  mappingId: number | null;
  mappingName: string | null;
  databaseName: string;
  dryRunEndTime: Date;
  dryRunStartTime: Date;
  dryRunStatus: ImportRunStatus;
  fileName: string;
  runEndTime: Date;
  runStartTime: Date;
  runStatus: ImportRunStatus;
  updatedAt: Date | null;
  updatedBy: IdentityUserModel | null;
  createdAt: Date;
  createdBy: IdentityUserModel;
  latestErrorCode: ImportRunError | null;
}

export interface IdentityUserModel {
  id: string;
  firstName: string;
  lastName: string;
  userName: string;
  avatar: string | null;
}

export type ImportRunStatus =
  | 'NotStarted'
  | 'Queued'
  | 'InProgress'
  | 'Failed'
  | 'Succeeded'
  | 'CancellationRequested'
  | 'Cancelled';

export const importRunStatusMap: Record<ImportRunStatus, string> = {
  NotStarted: 'Not started',
  Queued: 'Queued',
  InProgress: 'In progress',
  Failed: 'Failed',
  Succeeded: 'Succeeded',
  CancellationRequested: 'Cancellation requested',
  Cancelled: 'Cancelled'
};

const ACTIVE_IMPORT_STATUSES: ImportRunStatus[] = ['Queued', 'InProgress', 'CancellationRequested'];

/** True while a run is queued, executing or cancelling — i.e. the UI must keep polling. */
export const isActiveImportStatus = (status?: ImportRunStatus | null) =>
  !!status && ACTIVE_IMPORT_STATUSES.includes(status);

export type ImportRunError =
  | 'INVALID_FILE'
  | 'IMPORT_BLOCKED'
  | 'UNKNOWN_ERROR'
  | 'USER_CANCELLED'
  | 'INVALID_MAPPING'
  | 'INSUFFICIENT_PERMISSIONS';

export const importRunErrorMap: Record<ImportRunError, string> = {
  INVALID_FILE: 'the selected file is invalid, please try a different one',
  IMPORT_BLOCKED: 'this import was blocked, please review the errors shown below',
  UNKNOWN_ERROR: 'this import failed with an unknown error, please try again or contact support',
  USER_CANCELLED: 'this import was cancelled by the user',
  INVALID_MAPPING: 'the selected mapping is invalid, please try a different one',
  INSUFFICIENT_PERMISSIONS: 'the user does not have the sufficient permissions to run this import'
};

export interface CreateImportPayload {
  projectId: string;
  mappingId: string;
  file: File;
}

export interface ImportRunResult {
  attemptNumber: number;
  completeDateTime: Date;
  createdBy: IdentityUserModel;
  errorDetails: string[];
  isBlocked: boolean;
  isDryRun: boolean;
  nodesCreated: number;
  nodesFound: number;
  nodesSkipped: number;
  nodesUpdated: number;
  relationsCreated: number;
  relationsFound: number;
  relationsSwapped: number;
  rowsProcessed: number;
  rowsTotal: number;
  startDateTime: Date;
  lastErrorCode: ImportRunError | null;
  status: ImportRunStatus;
}
